import { useEffect, useRef, useState, type FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { groupMessages, sendGroupMessage, leaveGroup, type Group as G, type GroupMessage, type GroupParticipant } from "../lib/groupchat";

export function Group() {
  const { id } = useParams();
  const [group, setGroup] = useState<G | null>(null);
  const [participants, setParticipants] = useState<GroupParticipant[]>([]);
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [me, setMe] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  async function refresh() {
    if (!id) return;
    try {
      const d = await groupMessages(id);
      setGroup(d.group);
      setParticipants(d.participants);
      setMessages(d.messages);
      setMe(d.me);
    } catch {
      setErr("You're not a participant of this group.");
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void refresh(); }, [id]);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [messages]);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!id || !body.trim()) return;
    setBusy(true);
    try {
      await sendGroupMessage(id, body);
      setBody("");
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  if (err) return <div className="mx-auto max-w-2xl p-6"><p className="font-mono text-sm text-danger">{err}</p><Link to="/groups" className="text-accent-strong">← Group chats</Link></div>;
  if (!group) return <p className="p-6 font-mono text-sm text-muted">Loading…</p>;

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-4">
      <PageHeader
        title={group.title}
        subtitle={`${participants.length} participants · ${participants.map((p) => p.name).filter(Boolean).slice(0, 4).join(", ")}`}
        actions={<Button variant="ghost" onClick={async () => { if (id) { await leaveGroup(id); history.back(); } }}>Leave</Button>}
      />
      <Link to="/groups" className="font-mono text-[11px] text-accent-strong">← All group chats</Link>

      <div className="grid gap-2 rounded-card border border-line bg-surface p-4">
        {messages.length === 0 ? (
          <p className="text-sm text-muted">No messages yet. Say hello.</p>
        ) : messages.map((m) => (
          <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.sender_id === me ? "justify-self-end bg-accent/15" : "bg-surface-2"}`}>
            {m.sender_id !== me && <span className="block font-mono text-[10px] font-bold text-muted">{m.sender_name ?? "Member"}</span>}
            <span className="whitespace-pre-wrap">{m.body}</span>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form className="flex gap-2" onSubmit={send}>
        <input className={inputClass} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message the group…" />
        <Button type="submit" disabled={busy || !body.trim()}>Send</Button>
      </form>
    </div>
  );
}
